import React, { ReactElement } from "react";
import { sanitiseString, concatStringFromClassesArrayList } from "../../Utils";
import { StyledTableRow } from "./TableStyles";

export type TableVariant = "compact" | "responsive";

export interface Props {
  id: string;
  dataTestId?: string;
  caption?: string;
  hideCaption?: boolean;
  tableClasses?: string;
  variants?: Array<TableVariant> | TableVariant | string;
  ths: TableHeader[];
  trs: TableRow[];
  hideHeader?: boolean;
}

export interface TableHeader {
  value: string;
  id?: string;
  valign?: "top" | "middle" | "bottom";
  thClasses?: string;
  numeric?: boolean;
}

export interface TableRow{
  tds: TableCell[];
  id?: string;
  disabled?: boolean;
}

export interface TableCell {
  value?: string | ReactElement;
  tdClasses?: string;
  id?: string;
  valign?: "top" | "middle" | "bottom";
  numeric?: boolean;
}

export const Table = (props: Props) => {
  const sanitisedId = sanitiseString(props.id);
  const sanitisedDataTestId = sanitiseString(props.dataTestId);

  const isResponsive = props.variants?.includes("responsive") || false;

  const tableClassList = {
    defaultClass: "ons-table",
    optionalClasses: [
      props.tableClasses ? props.tableClasses : null,
      props.variants?.includes("compact") ? "ons-table--compact" : null,
      isResponsive ? "ons-table--responsive" : null,
    ],
  };

  const tableClassName = concatStringFromClassesArrayList(tableClassList);

  const tableHeadClassList = {
    defaultClass: "ons-table__head",
    optionalClasses: [props.hideHeader ? "ons-u-vh" : null],
  };

  const tableHeadClassName =
    concatStringFromClassesArrayList(tableHeadClassList);

  const captionClassList = {
    defaultClass: "ons-table__caption",
    optionalClasses: [props.hideCaption ? "ons-u-vh" : null],
  };

  const captionClassName = concatStringFromClassesArrayList(captionClassList);

  const getHeaderClassName = (th: TableHeader): string =>
    concatStringFromClassesArrayList({
      defaultClass: "ons-table__header",
      optionalClasses: [
        `ons-table__header--${th.valign || "top"}`,
        th.thClasses || null,
        th.numeric ? "ons-table__header--numeric" : null,
      ],
    });

  const getCellClassName = (td: TableCell): string =>
    concatStringFromClassesArrayList({
      defaultClass: "ons-table__cell",
      optionalClasses: [
        `ons-table__cell--${td.valign || "top"}`,
        td.tdClasses || null,
        td.numeric ? "ons-table__cell--numeric" : null,
      ],
    });

  const TableElement = () => (
    <table
      id={sanitisedId}
      data-testid={sanitisedDataTestId}
      className={tableClassName}
    >
      {props.caption && (
        <caption
          className={captionClassName}
          data-testid={`${sanitisedDataTestId}-caption`}
        >
          {props.caption}
        </caption>
      )}
      <thead
        className={tableHeadClassName}
        data-testid={`${sanitisedDataTestId}-head`}
      >
        <tr className="ons-table__row">
          {props.ths.map((th, index) => (
            <th key={index} scope="col" className={getHeaderClassName(th)}>
              <span className="ons-table__header-text">{th.value}</span>
            </th>
          ))}
        </tr>
      </thead>
      <tbody
        className="ons-table__body"
        data-testid={`${sanitisedDataTestId}-body`}
      >
        {props.trs.map((tr, trIndex) => (
          <StyledTableRow
            key={trIndex}
            className="ons-table__row"
            id={tr.id}
            $disabled={tr.disabled || false}
          >
            {tr.tds.map((td, tdIndex) => (
              <td
                id={td.id}
                key={tdIndex}
                className={getCellClassName(td)}
                {...(isResponsive && props.ths[tdIndex]?.value
                  ? { "data-th": props.ths[tdIndex].value }
                  : {})}
              >
                {td.value}
              </td>
            ))}
          </StyledTableRow>
        ))}
      </tbody>
    </table>
  );

  if (isResponsive) {
    return (
      <div className="ons-table-scrollable ons-table-scrollable--on">
        <div
          className="ons-table-scrollable__content"
          tabIndex={0}
          role="region"
          aria-label={
            props.caption
              ? `${props.caption}. Scrollable table`
              : "Scrollable table"
          }
          data-testid={
            sanitisedDataTestId
              ? `${sanitisedDataTestId}-scrollable`
              : undefined
          }
        >
          <TableElement />
        </div>
      </div>
    );
  }

  return <TableElement />;
};
